import * as React from "react";
import { Text, Select, Input, Button, Flex, FieldText } from "@theorgchartdev/storybook";
import { GetWSMgr } from "@/Utils/StateLib";
import SectionCard, { SectionCardGroup } from "../../../../Composites/SectionCard/SectionCard";
import { PLACEHOLDER_TABLE_VALUE } from "@/Utils/Constants";
import { enumFeaturesList, enumFileMgrMode } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { getFeatureAccess } from "@/Utils/Misc";

const GetLaunchCopy = launchConfig => {
  return JSON.parse(JSON.stringify(launchConfig));
};

export const GeneralConfiguration = ({ accountConfig, handleUpdate }) => {
  const [showExplorer, setShowExplorer] = React.useState(false);

  if (!accountConfig) return;

  const accountInfo = accountConfig.account;
  let launchConfig = { mode: "Viewer" };
  if (accountConfig.launch) {
    launchConfig = accountConfig.launch;
  }

  const HandleInput = e => {
    handleUpdate({ name: e.target.value }, "name");
  };

  // Launch Mode: Viewing or Directory
  const HandleModeSelect = e => {
    const targetMode = e.value;
    let newLaunchConfig = GetLaunchCopy(launchConfig);
    if (targetMode === "Directory") newLaunchConfig["enabled"] = false;
    newLaunchConfig["mode"] = targetMode;
    handleUpdate({ launch: newLaunchConfig }, "launch");
  };

  // Changing between base chart and custom chart
  const HandleTypeSelect = e => {
    const type = e.value;
    let newLaunchConfig = GetLaunchCopy(launchConfig);
    if (type == "customchart") {
      newLaunchConfig["directory"] = "";
      newLaunchConfig["filename"] = "";
      newLaunchConfig["enabled"] = true;
    } else {
      //Not trying to launch with a document
      newLaunchConfig["enabled"] = false;
    }
    handleUpdate({ launch: newLaunchConfig }, "launch");
  };

  const HandleChartSelect = e => {
    handleUpdate({ defaultchart: e.value }, "defaultchart");
  };

  const ToggleFileDirectory = () => {
    const wsMgr = GetWSMgr();
    wsMgr.DoFileMgr(enumFileMgrMode.selectcustomchart, {
      customOnSelect: SelectCustomChart,
      launchDirectory: launchConfig?.directory,
      currentSelectedFile: launchConfig?.document
    });
  };

  //event is a QFile obj
  const SelectCustomChart = (dirname, filename) => {
    let newLaunchConfig = GetLaunchCopy(launchConfig);
    newLaunchConfig["enabled"] = true;
    newLaunchConfig["document"] = filename;
    newLaunchConfig["directory"] = dirname;
    handleUpdate({ launch: newLaunchConfig }, "launch");
  };

  const DoActivityReport = () => {
    let wsMgr = GetWSMgr();
    wsMgr.DoActivityReport();
  };

  const launchOptions = [
    {
      label: appstr("Viewing Chart"),
      value: "Viewer",
      clickHandler: HandleModeSelect
    },
    {
      label: appstr("Directory"),
      value: "Directory",
      clickHandler: HandleModeSelect
    }
  ];
  const chartTypeOptions = [
    {
      label: appstr("Base Chart"),
      value: "basechart",
      clickHandler: HandleTypeSelect
    },
    {
      label: appstr("Custom Chart"),
      value: "customchart",
      clickHandler: HandleTypeSelect
    }
  ];

  const BuildSelectOptions = (charts, changeFunction) => {
    let options = [];
    for (let idx in charts) {
      options.push({
        label: charts[idx].name,
        value: charts[idx].ID,
        clickHandler: changeFunction
      });
    }

    return options;
  };

  const GetLaunchFileString = () => {
    const launchFile = launchConfig["document"];
    const launchDir = launchConfig["directory"];
    let ppFileString =
      launchFile && launchDir && launchDir !== "MONGO"
        ? launchDir + " \\ " + launchFile
        : appstr("Open file explorer");
    if (ppFileString.includes(".oc5") || ppFileString.includes(".ocd"))
      ppFileString = ppFileString.split(".")[0];
    return ppFileString;
  };

  var launchMode = launchConfig["mode"];
  if (!["Viewer", "Workbench", "Directory"].includes(launchMode)) {
    launchMode = "Viewer";
  }
  var launchMasterChart = launchMode != "Workbench" ? !launchConfig["enabled"] : false;
  //The above if condition should always be true.
  if (launchMode === "Workbench") launchMasterChart = false;
  const launchType = launchMasterChart ? "basechart" : "customchart";
  var disabledBaseChartSelect =
    accountConfig.charts.length <= 1 || !getFeatureAccess(enumFeaturesList.defaultLaunchMode);

  return (
    <SectionCardGroup>
      <SectionCard title={appstr("Launch Settings")}>
        <Flex direction={"column"} gap={"16px"}>
          <Flex direction={"column"} gap={"8px"} width={"400px"}>
            <Text size="small" modifier={"medium"}>
              {appstr("Default mode")}
            </Text>
            <Select
              id="launch-select"
              dataTestId="account-settings-general-launch-mode-select"
              listWidth="small"
              selectSize={"medium"}
              value={launchMode}
              options={launchOptions}
              disabled={!getFeatureAccess(enumFeaturesList.defaultLaunchMode)}
            />
          </Flex>
          <Flex direction={"column"} gap={"8px"} width={"400px"}>
            <Text size={"small"} modifier={"medium"}>
              {appstr("Launch chart")}
            </Text>
            <Select
              id="chart-type-select"
              dataTestId="account-settings-general-launch-chart-type-select"
              listWidth="small"
              selectSize={"medium"}
              value={launchType}
              options={chartTypeOptions}
              disabled={!getFeatureAccess(enumFeaturesList.defaultLaunchMode)}
            />
            {launchType === "basechart" ? (
              <Select
                id="basechart-select"
                dataTestId="account-settings-general-launch-basechart-select"
                listWidth="small"
                selectSize={"medium"}
                disabled={disabledBaseChartSelect}
                value={accountConfig.defaultchart}
                options={BuildSelectOptions(accountConfig.charts, HandleChartSelect)}
              />
            ) : (
              <Button
                modifier="secondary"
                className={"oc-special-secondary"}
                size="medium"
                isFullWidth={false}
                onClick={ToggleFileDirectory}
                disabled={!getFeatureAccess(enumFeaturesList.defaultLaunchMode)}
                dataTestId="account-settings-general-launch-custom-chart-btn"
              >
                {GetLaunchFileString()}
              </Button>
            )}
          </Flex>
        </Flex>
      </SectionCard>
      <SectionCard
        title={appstr("Administration")}
        titleContent={
          <Button
            modifier="secondary"
            size="medium"
            className={"oc-special-secondary"}
            isFullWidth={false}
            onClick={DoActivityReport}
            disabled={!getFeatureAccess(enumFeaturesList.activityReport)}
            dataTestId="account-settings-general-download-activity-report-btn"
          >
            {appstr("Download activity report")}
          </Button>
        }
      >
        <Flex direction={"column"} gap={"12px"}>
          <Flex direction={"column"} gap={"8px"} width={"400px"}>
            <Text size={"small"} modifier={"medium"}>
              {appstr("Company Name")}
            </Text>
            <Input
              style={{ marginBottom: "0" }}
              id="company-name-input"
              dataTestId="account-settings-general-company-name-input"
              size="medium"
              value={accountConfig.name}
              onChange={e => HandleInput(e)}
            />
          </Flex>
          <Flex direction={"column"} gap={"12px"}>
            <FieldText
              field={appstr("Organization ID")}
              placeHolder={PLACEHOLDER_TABLE_VALUE}
              size="medium"
              value={accountConfig.orgname}
            />
            <FieldText
              field={appstr("boxlimit")}
              placeHolder={PLACEHOLDER_TABLE_VALUE}
              size="medium"
              value={accountInfo?.boxLimit}
            />
            <FieldText
              field={appstr("seatcount")}
              placeHolder={PLACEHOLDER_TABLE_VALUE}
              size="medium"
              value={accountInfo.seatCount}
            />
          </Flex>
        </Flex>
      </SectionCard>
    </SectionCardGroup>
  );
};
